import React, { useState, useEffect } from 'react';
import { fetchCollection } from '../lib/cms';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

export default function TestimonialSlider() {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTestimonials() {
      const data = await fetchCollection('testimonials', { sort: 'date', order: 'desc' });
      if (data && data.items) {
        setTestimonials(data.items);
      }
      setLoading(false);
    }
    loadTestimonials();
  }, []);

  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full max-w-7xl mx-auto px-4 py-8">
      {/* Desktop & Tablet Grid View with sliding behavior */}
      <div className="relative overflow-hidden">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {/* We chunk or map them. To make it responsive, we can render them as a grid that changes columns, or we can render a sliding track of cards. Let's make a beautiful responsive grid of cards that slides, or let's show a 3-column layout on desktop, 2 on tablet, 1 on mobile. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full shrink-0">
            {testimonials.slice(0, 3).map((item) => (
              <div 
                key={item.id} 
                className="relative flex flex-col justify-between rounded-2xl border border-slate-100 bg-white p-6 shadow-md hover:shadow-lg transition-all"
              >
                <Quote className="absolute top-6 right-6 h-10 w-10 text-slate-100 shrink-0 pointer-events-none" />
                <div>
                  <div className="flex items-center gap-1 text-brand-gold mb-4">
                    {[...Array(item.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <div 
                    className="text-slate-600 text-sm leading-relaxed mb-6 italic"
                    dangerouslySetInnerHTML={{ __html: item.content.da }}
                  />
                </div>
                <div className="flex items-center justify-between border-t border-slate-50 pt-4">
                  <div>
                    <h4 className="font-display font-bold text-brand-charcoal text-sm">
                      {item.author.da}
                    </h4>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Anbefalet via {item.source || '3byg.dk'}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-brand-primary bg-brand-primary/5 px-2.5 py-1 rounded-full">
                    {new Date(item.date).toLocaleDateString('da-DK', { year: 'numeric', month: 'short' })}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {testimonials.length > 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full shrink-0">
              {testimonials.slice(3, 6).map((item) => (
                <div 
                  key={item.id} 
                  className="relative flex flex-col justify-between rounded-2xl border border-slate-100 bg-white p-6 shadow-md hover:shadow-lg transition-all"
                >
                  <Quote className="absolute top-6 right-6 h-10 w-10 text-slate-100 shrink-0 pointer-events-none" />
                  <div>
                    <div className="flex items-center gap-1 text-brand-gold mb-4">
                      {[...Array(item.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-current" />
                      ))}
                    </div>
                    <div 
                      className="text-slate-600 text-sm leading-relaxed mb-6 italic"
                      dangerouslySetInnerHTML={{ __html: item.content.da }}
                    />
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-50 pt-4">
                    <div>
                      <h4 className="font-display font-bold text-brand-charcoal text-sm">
                        {item.author.da}
                      </h4>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Anbefalet via {item.source || '3byg.dk'}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-brand-primary bg-brand-primary/5 px-2.5 py-1 rounded-full">
                      {new Date(item.date).toLocaleDateString('da-DK', { year: 'numeric', month: 'short' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Controls */}
      {testimonials.length > 3 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={prevSlide}
            className="p-2 rounded-full border border-slate-200 bg-white text-brand-charcoal hover:bg-slate-50 transition-colors cursor-pointer shadow-sm"
            aria-label="Forrige anmeldelser"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-1.5">
            {[...Array(Math.ceil(testimonials.length / 3))].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2.5 w-2.5 rounded-full transition-all cursor-pointer ${
                  currentIndex === i ? 'bg-brand-primary w-6' : 'bg-slate-200 hover:bg-slate-300'
                }`}
                aria-label={`Gå til side ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={nextSlide}
            className="p-2 rounded-full border border-slate-200 bg-white text-brand-charcoal hover:bg-slate-50 transition-colors cursor-pointer shadow-sm"
            aria-label="Næste anmeldelser"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
